import React, { useContext } from "react";
import { useFormContext } from "react-hook-form";
import { formatCollection } from "common/utils/strings";
import { Section } from "../../../../../../../components/Common/Layout";
import { UserGroup } from "../../../../../../../generated/graphql";
import { spacing } from "../../../../../../../stitches.config";
import { AuthContext } from "../../../../../../Authorization/AuthContext";
import { Button } from "../../../../../../Common/Button";
import { Icon, IconNames } from "../../../../../../Common/Icons/LucideIcons";
import { Tooltip } from "../../../../../../Common/Tooltip";
import { Body } from "../../../../../../Common/Typography";
import { BuilderFieldEditButtons } from "../../__styles__/SubmissionsBuilder";
import { VIEWER_TYPE, viewerTypeToFieldPermissionsMap } from "../../constants";
import { SubmissionsBuilderContextInstance } from "../../context";
import {
  generateLabelsForRoles,
  partitionRolesAndStrings,
} from "../../RoleAndGroupSelector";
import {
  FieldActionsUpdaterProps,
  InputFieldPath,
  SubmissionsBuilderFormDataStructure,
} from "../../types";
import {
  getInputOptionFromFieldPermissions,
  withStopPropagation,
} from "../../utils";

export type FieldPreviewFooterProps = FieldActionsUpdaterProps & {
  isGroup?: boolean;
  fieldPath: InputFieldPath;
  userGroups: UserGroup[];
};

const FieldPreviewFooter = (props: FieldPreviewFooterProps) => {
  const { fieldPath, isGroup, userGroups } = props;

  return (
    <footer>
      <Section flexDirection="row" style={{ justifyContent: "space-between" }}>
        <div style={{ flexGrow: 1 }}>
          {!isGroup && fieldPath && (
            <CreatorPermissionsInfo
              fieldPath={fieldPath}
              userGroups={userGroups}
            />
          )}
        </div>
        <FieldActions {...props} />
      </Section>
    </footer>
  );
};

const CreatorPermissionsInfo = ({
  fieldPath,
  userGroups,
}: {
  fieldPath: InputFieldPath;
  userGroups: UserGroup[];
}) => {
  const { watch } = useFormContext<SubmissionsBuilderFormDataStructure>();

  const viewOnCreate = watch(`${fieldPath}.viewOnCreate`);

  const [roles, selectedUserGroups] = partitionRolesAndStrings(
    viewOnCreate ?? [],
  );

  const roleLabels = generateLabelsForRoles(roles);
  const groupLabels = userGroups
    .filter(userGroup => selectedUserGroups.includes(userGroup.id))
    .map(userGroup => userGroup.name);

  const combinedLabel = formatCollection([...roleLabels, ...groupLabels]);

  if (!combinedLabel) {
    return null;
  }

  return (
    <Section
      flexDirection="row"
      gap={spacing.xs}
      style={{ paddingTop: spacing["2xs"].value, flexGrow: 2 }}
    >
      <Icon color="contentSecondary" iconName="clipboard-pen" size={16} />
      <Body
        size="small"
        type="regular"
        testId={`${fieldPath}_creator-permissions-info`}
      >
        {combinedLabel}
      </Body>
    </Section>
  );
};

const FieldActions = ({
  canMoveUp,
  canMoveDown,
  canDelete,
  moveFieldUp,
  moveFieldDown,
  duplicateField,
  deleteField,
  isGroup,
  fieldPath,
}: FieldPreviewFooterProps) => {
  const { readOnly } = useContext(SubmissionsBuilderContextInstance);
  const labelTarget = isGroup ? "group" : "field";

  if (readOnly) {
    return null;
  }

  return (
    <BuilderFieldEditButtons>
      {!isGroup && <ToggleViewerPermissions fieldPath={fieldPath} />}
      {canMoveUp && (
        <li>
          <Button
            styleVariant="ghost"
            onClick={withStopPropagation(moveFieldUp)}
            aria-label={`Move ${labelTarget} up`}
          >
            <Tooltip
              content={
                <Icon color="contentSecondary" iconName="arrow-up" size={16} />
              }
              popoverContent="Move up"
            />
          </Button>
        </li>
      )}
      {canMoveDown && (
        <li>
          <Button
            styleVariant="ghost"
            onClick={withStopPropagation(moveFieldDown)}
            aria-label={`Move ${labelTarget} down`}
          >
            <Tooltip
              content={
                <Icon
                  color="contentSecondary"
                  iconName="arrow-down"
                  size={16}
                />
              }
              popoverContent="Move down"
            />
          </Button>
        </li>
      )}
      <li>
        <Button
          styleVariant="ghost"
          onClick={withStopPropagation(duplicateField)}
          aria-label={`Duplicate ${labelTarget}`}
        >
          <Tooltip
            content={
              <Icon color="contentSecondary" iconName="copy" size={16} />
            }
            popoverContent="Duplicate"
          />
        </Button>
      </li>
      {canDelete && (
        <li>
          <Button
            styleVariant="ghost"
            onClick={withStopPropagation(deleteField)}
            aria-label={`Delete ${labelTarget}`}
          >
            <Tooltip
              content={<Icon color="contentSecondary" iconName="x" size={16} />}
              popoverContent="Delete"
            />
          </Button>
        </li>
      )}
    </BuilderFieldEditButtons>
  );
};

const ToggleViewerPermissions = ({
  fieldPath,
}: {
  fieldPath: InputFieldPath;
}) => {
  const { getValues, setValue } =
    useFormContext<SubmissionsBuilderFormDataStructure>();
  const { account } = useContext(AuthContext);
  const publicPortalEnabled = account?.publicPortal.enabled;

  const currPermissions = getValues(`${fieldPath}.viewOnRetrieve`);
  const currOption = getInputOptionFromFieldPermissions(
    currPermissions ?? [],
    viewerTypeToFieldPermissionsMap,
  );

  const toggleViewerPermissions = () => {
    const updatedInputOption =
      currOption === VIEWER_TYPE.TEAM ?
        VIEWER_TYPE.TEAM_AND_GUESTS
      : VIEWER_TYPE.TEAM;
    const updatedPermissions =
      viewerTypeToFieldPermissionsMap[updatedInputOption];
    setValue(`${fieldPath}.viewOnRetrieve`, updatedPermissions);
  };

  const iconMap: Record<VIEWER_TYPE, IconNames> = {
    [VIEWER_TYPE.TEAM]: "eye-off",
    [VIEWER_TYPE.TEAM_AND_GUESTS]: "eye",
  };

  const currIcon = currOption ? iconMap[currOption] : null;

  const verb = currOption === VIEWER_TYPE.TEAM ? "on" : "off";

  const tooltipText =
    currOption === VIEWER_TYPE.TEAM ?
      "Visible to guests after submitted"
    : "Hidden from guests after submitted";

  if (!currIcon) {
    return null;
  }

  return (
    <li>
      <Button
        styleVariant="ghost"
        onClick={withStopPropagation(toggleViewerPermissions)}
        aria-label={`Toggle ${verb} public viewer permissions`}
        data-testid={`${fieldPath}_toggle-viewer-permissions`}
        disabled={!publicPortalEnabled}
      >
        <Tooltip
          content={
            <Icon color="contentSecondary" iconName={currIcon} size={16} />
          }
          popoverContent={tooltipText}
        />
      </Button>
    </li>
  );
};

export default FieldPreviewFooter;
